import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./baseQuery";

export const hatimManagementApi = createApi({
  reducerPath: "panelHatimManagement",
  baseQuery: axiosBaseQuery(),
  // Sekmeye geri dönüşte ve bağlantı gelince yenile; bir bileşen mount olduğunda
  // cache 30 sn'den eskiyse tekrar çek. Böylece sayfa yenilemeye gerek kalmaz.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: 30,
  tagTypes: ["MyHatimTemplates", "HatimTemplate", "HatimProgress"],
  endpoints: (builder) => ({
    getManagedHatimTemplates: builder.query({
      query: () => ({
        url: "/api/panel/hatim-templates/get-managed",
        method: "POST",
        data: {},
      }),
      providesTags: ["MyHatimTemplates"],
    }),
    getManagedHatimTemplate: builder.query({
      query: ({ hatimTemplateId }) => ({
        url: "/api/panel/hatim-templates/get-managed-detail",
        method: "POST",
        data: { hatimTemplateId },
      }),
      providesTags: (result, error, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
      ],
    }),
    // Mevcut mobil rotası: yönetici olunan tüm şablonların dönem/üye ilerlemesi.
    // JWT ile çalışır (imza istemez); şablon bazlı görünüm istemcide kurulur.
    getMyHatimInstanceGroups: builder.query({
      query: () => ({
        url: "/api/hatim-instances/get-my-hatim-instance-groups",
        method: "POST",
        data: {},
      }),
      providesTags: ["HatimProgress"],
    }),
    createHatimTemplate: builder.mutation({
      query: (body) => ({
        url: "/api/panel/hatim-templates/create",
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["MyHatimTemplates", "HatimProgress"],
    }),
    removeHatimTemplate: builder.mutation({
      query: ({ hatimTemplateId }) => ({
        url: "/api/panel/hatim-templates/delete",
        method: "POST",
        data: { hatimTemplateId },
      }),
      invalidatesTags: ["MyHatimTemplates", "HatimProgress"],
    }),
    updateHatimRepeat: builder.mutation({
      query: ({ hatimTemplateId, repeatDuration }) => ({
        url: "/api/panel/hatim-templates/update-repeat",
        method: "POST",
        data: { hatimTemplateId, repeatDuration },
      }),
      invalidatesTags: (r, e, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
        "MyHatimTemplates",
      ],
    }),
    addHatimManager: builder.mutation({
      query: ({ hatimTemplateId, email }) => ({
        url: "/api/panel/hatim-templates/add-manager",
        method: "POST",
        data: { hatimTemplateId, email },
      }),
      invalidatesTags: (r, e, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
        "MyHatimTemplates",
      ],
    }),
    removeHatimManager: builder.mutation({
      query: ({ hatimTemplateId, email }) => ({
        url: "/api/panel/hatim-templates/remove-manager",
        method: "POST",
        data: { hatimTemplateId, email },
      }),
      invalidatesTags: (r, e, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
        "MyHatimTemplates",
      ],
    }),
    addHatimUser: builder.mutation({
      query: ({ hatimTemplateId, email, assignedAmount, assignedPageRanges }) => ({
        url: "/api/panel/hatim-templates/add-user",
        method: "POST",
        data: { hatimTemplateId, email, assignedAmount, assignedPageRanges },
      }),
      invalidatesTags: (r, e, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
        "MyHatimTemplates",
        "HatimProgress",
      ],
    }),
    removeHatimUser: builder.mutation({
      query: ({ hatimTemplateId, email }) => ({
        url: "/api/panel/hatim-templates/remove-user",
        method: "POST",
        data: { hatimTemplateId, email },
      }),
      invalidatesTags: (r, e, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
        "MyHatimTemplates",
        "HatimProgress",
      ],
    }),
    updateHatimUserAssignment: builder.mutation({
      query: ({ hatimTemplateId, email, assignedAmount, assignedPageRanges }) => ({
        url: "/api/panel/hatim-templates/update-user-assignment",
        method: "POST",
        data: { hatimTemplateId, email, assignedAmount, assignedPageRanges },
      }),
      invalidatesTags: (r, e, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
        "MyHatimTemplates",
        "HatimProgress",
      ],
    }),
    addParticipationTemplate: builder.mutation({
      query: ({ hatimTemplateId, maxCount }) => ({
        url: "/api/panel/hatim-templates/add-participation",
        method: "POST",
        data: { hatimTemplateId, maxCount },
      }),
      invalidatesTags: (r, e, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
      ],
    }),
    removeParticipationTemplate: builder.mutation({
      query: ({ hatimTemplateId, code }) => ({
        url: "/api/panel/hatim-templates/remove-participation",
        method: "POST",
        data: { hatimTemplateId, code },
      }),
      invalidatesTags: (r, e, { hatimTemplateId }) => [
        { type: "HatimTemplate", id: hatimTemplateId },
      ],
    }),
  }),
});

export const {
  useGetManagedHatimTemplatesQuery,
  useGetManagedHatimTemplateQuery,
  useGetMyHatimInstanceGroupsQuery,
  useCreateHatimTemplateMutation,
  useRemoveHatimTemplateMutation,
  useUpdateHatimRepeatMutation,
  useAddHatimManagerMutation,
  useRemoveHatimManagerMutation,
  useAddHatimUserMutation,
  useRemoveHatimUserMutation,
  useUpdateHatimUserAssignmentMutation,
  useAddParticipationTemplateMutation,
  useRemoveParticipationTemplateMutation,
} = hatimManagementApi;
